// A dance, danced: an anonymous plazmi (soft grey-cream, no name tag) on a
// little round stage, dancing a dance's beats on a loop with the real moves
// (moves.ts poses, rig.ts nubs, fx.ts cues), the way Life dances them in a
// troupe: each beat its move at its manner's tempo, repeats spelled out,
// travelling moves stepping along and back. Its drum pattern is the dance's
// own (sound/beats.ts, from its name), and plays only once sound is on (after
// a click) and only while the dance plays. The dance's page and the inline
// demos on the noticeboard and the Gazette use it; dispose() frees it all.
import * as THREE from 'three';
import { govern, Q } from './quality.js';
import { failed } from './loading.js';
import { probeRenderer } from './perf/probe.js';
import { makeStage } from './stage.js';
import { finishShadows } from './kit.js';
import { plazmi } from './chars.js';
import { Fx } from './fx.js';
import { BEAT, moveOf, poseAt, cuesBetween, repSecs, ampOf, tempoOf, neutral, type Pose } from './moves.js';
import { rigOf, poseRig, type Rig } from './rig.js';
import { engine, gain } from './sound/engine.js';
import { patternFor, playStep, playFill, swingOf, accentOf, type Kit, type Pattern } from './sound/beats.js';
import { mountControl } from './sound/control.js';

export type Beat = { move: string; how?: string; times?: number; label?: string };
export type DemoOpts = { name: string; still?: boolean; kit?: Kit; onBeat?: (i: number) => void; maxPixelRatio?: number };

const PIVOT = 0.48;
const BODY = '#e6dfd2', GLOW = '#d9e4e8'; // a neutral plazmi: nobody in particular
const SLOW = new Set(['slowly', 'lazily', 'sleepily', 'dreamily', 'gently', 'softly']);

// the dance laid out on its own clock, one loop long: a slice per beat, then a short rest
type Slice = { i: number; move: string; t0: number; rep: number; reps: number; tempo: number; amp: number; offs: [number, number][]; dirs: number[] };
type Step = { at: number; idx: number; drop: boolean; accent: string | null; fill: boolean; stepSecs: number };

function layout(beats: Beat[]) {
  const slices: Slice[] = [];
  let t = 0, off: [number, number] = [0, 0];
  const REACH = 0.9;
  for (const [i, b] of beats.slice(0, 24).entries()) {
    const tempo = tempoOf(b.how), reps = Math.max(1, Math.min(4, Math.round(b.times || 1))), rep = repSecs(b.move, tempo);
    const tr = moveOf(b.move).travel, offs: [number, number][] = [], dirs: number[] = [];
    for (let k = 0; k < reps; k++) {
      offs.push([off[0], off[1]]);
      let dir = 1;
      if (tr) {
        if (Math.hypot(off[0] + tr[0], off[1] + tr[1]) > REACH) dir = -1;
        off = [off[0] + tr[0] * dir, off[1] + tr[1] * dir];
      }
      dirs.push(dir);
    }
    slices.push({ i, move: b.move, t0: t, rep, reps, tempo, amp: ampOf(tempo), offs, dirs });
    t += rep * reps;
  }
  // a rest of two beats, walking back to the middle, before it goes round again
  const rest = { i: -1, move: 'bob', t0: t, rep: BEAT * 2, reps: 1, tempo: 1, amp: ampOf(1), offs: [off], dirs: [1] } as Slice;
  slices.push(rest);
  const loop = t + rest.rep;
  // the drum steps for one loop: four to a beat, at each slice's tempo
  const steps: Step[] = [];
  let base = 0;
  slices.forEach((s, n) => {
    const beatsIn = Math.round((s.rep * s.reps) / (BEAT / s.tempo)), st = BEAT / s.tempo / 4, count = beatsIn * 4;
    const next = slices[n + 1];
    const drop = SLOW.has(beats[s.i]?.how || '') || s.tempo < 0.75;
    for (let k = 0; k < count; k++) {
      const acc = k === 0 ? accentOf(s.move) : null;
      steps.push({ at: s.t0 + k * st, idx: base + k, drop, accent: acc === 'fill' ? null : acc, fill: k === count - 2 && !!next && accentOf(next.move) === 'fill', stepSecs: st });
    }
    base += count;
  });
  return { slices, loop, steps };
}

class Body {
  root = new THREE.Group(); base = new THREE.Group(); squash = new THREE.Group(); spin = new THREE.Group(); body = new THREE.Group();
  models = new Map<string, { m: THREE.Object3D; rig: Rig }>(); cur: { m: THREE.Object3D; rig: Rig } | null = null;
  constructor(scene: THREE.Scene) {
    this.root.add(this.base); this.base.add(this.squash); this.squash.add(this.spin); this.spin.add(this.body);
    this.spin.position.y = PIVOT; this.body.position.y = -PIVOT;
    scene.add(this.root);
  }
  wear(mood: string | null) {
    const key = String(mood);
    let v = this.models.get(key);
    if (!v) {
      const m = plazmi({ color: BODY, glow: GLOW, mood } as any);
      finishShadows(m); this.body.add(m);
      v = { m, rig: rigOf(m) }; this.models.set(key, v);
    }
    if (this.cur !== v) { if (this.cur) this.cur.m.visible = false; v.m.visible = true; this.cur = v; }
    return v;
  }
  apply(P: Pose, t: number) {
    const v = this.wear(P.mood);
    this.squash.scale.set(P.sx, P.sy, P.sx);
    this.squash.position.set(P.dx, P.y, P.dz);
    this.squash.rotation.set(P.rx, 0, P.rz);
    this.spin.rotation.set(P.px, P.py, P.pz);
    poseRig(v.rig, P, t);
  }
  head() { const p = new THREE.Vector3(0, 1.45 + this.squash.position.y, 0); this.base.localToWorld(p); return p; }
}

export class DanceDemo {
  readonly canvas: HTMLCanvasElement;
  playing = true;
  speed = 1;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private cam: THREE.PerspectiveCamera;
  private body: Body;
  private fx = new Fx();
  private plan: ReturnType<typeof layout>;
  private clock = 0; // seconds of dance danced
  private last = -1;
  private raf = 0;
  private lit = -2;
  private ro: ResizeObserver | null = null;
  private pattern: Pattern;
  private cursor = 0;
  private out: GainNode | null = null;
  private disposed = false;

  constructor(private host: HTMLElement, beats: Beat[], private o: DemoOpts) {
    this.plan = layout(beats.length ? beats : [{ move: 'dance' }]);
    this.pattern = patternFor(o.name, beats.map((b) => b.move), { tempo: tempoOf(beats[0]?.how) });
    let r: THREE.WebGLRenderer;
    try { r = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: !!o.still, alpha: false }); }
    catch (e) { failed(); throw e; }
    // one plazmi, drawn straight: the tier caps its pixels and frame rate, and its shadow (quality.ts)
    r.setPixelRatio(Math.min(window.devicePixelRatio || 1, o.maxPixelRatio ?? 2, o.still ? 2 : Q().prCap + 0.25));
    r.shadowMap.enabled = true; r.shadowMap.type = THREE.PCFShadowMap; r.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer = probeRenderer(r); this.canvas = r.domElement;
    host.appendChild(this.canvas);
    // (P108) a finger on the stage still scrolls the page, but a pinch over it never zooms the page
    this.canvas.style.touchAction = 'pan-x pan-y';
    const { scene } = makeStage(r, { sky: [[0, '#f7eedc'], [0.6, '#f1e2c8'], [1, '#e7d2b2']], shadowExtent: 3, shadowSize: 1024, sunDir: [-0.55, 1.1, 0.75] });
    scene.fog = null; this.scene = scene;
    // a round dance mat on a low wooden drum
    const drum = new THREE.Mesh(new THREE.CylinderGeometry(1.9, 1.95, 0.16, 56), new THREE.MeshStandardMaterial({ color: '#b98a5c', roughness: 0.85 }));
    drum.position.y = -0.08; drum.receiveShadow = true; scene.add(drum);
    const mat = new THREE.Mesh(new THREE.CircleGeometry(1.62, 56), new THREE.MeshStandardMaterial({ color: '#efe3cc', roughness: 1 }));
    mat.rotation.x = -Math.PI / 2; mat.position.y = 0.002; mat.receiveShadow = true; scene.add(mat);
    const ring = new THREE.Mesh(new THREE.RingGeometry(1.38, 1.46, 56), new THREE.MeshStandardMaterial({ color: '#c99a7a', roughness: 1 }));
    ring.rotation.x = -Math.PI / 2; ring.position.y = 0.004; ring.receiveShadow = true; scene.add(ring);
    this.body = new Body(scene);
    scene.add(this.fx.root);
    this.cam = new THREE.PerspectiveCamera(22, 1, 0.5, 100);
    this.fit();
    if (!o.still) this.warm();
    this.ro = new ResizeObserver(() => this.fit());
    this.ro.observe(host);
    if (o.still) {
      // a still: the dance's liveliest moment early on, the same every time
      const t = this.stillTime();
      this.step(0, t);
      this.renderer.render(this.scene, this.cam);
      return;
    }
    mountControl();
    const gov = govern({ renderer: r, scene, fixedPR: true });
    const loop = (now: number) => {
      if (this.disposed) return;
      if (!gov.frame(now)) { this.raf = requestAnimationFrame(loop); return; }
      const dt = this.last < 0 ? 0 : Math.min(0.1, (now - this.last) / 1000);
      this.last = now;
      if (this.playing) this.step(dt * this.speed, this.clock + dt * this.speed);
      else this.fx.update(dt);
      this.sound();
      this.renderer.render(this.scene, this.cam);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  private fit() {
    const w = Math.max(1, this.host.clientWidth), h = Math.max(1, this.host.clientHeight);
    this.renderer.setSize(w, h, false);
    this.canvas.style.width = '100%'; this.canvas.style.height = '100%';
    const asp = w / h, viewH = Math.max(2.5, 3.0 / asp);
    const dir = new THREE.Vector3(0.18, 0.42, 1).normalize(), centre = new THREE.Vector3(0, 0.9, 0);
    const d = viewH / 2 / Math.tan((this.cam.fov / 2) * Math.PI / 180);
    this.cam.aspect = asp; this.cam.position.copy(centre).addScaledVector(dir, d); this.cam.lookAt(centre); this.cam.updateProjectionMatrix();
    this.body.root.rotation.y = Math.atan2(dir.x, dir.z);
    if (this.o.still) this.renderer.render(this.scene, this.cam);
  }

  // every face the dance will pull, made and compiled now, so the first time each shows there's no hitch
  private warm() {
    const moods = new Set<string | null>();
    for (const s of this.plan.slices) for (let k = 0; k < 8; k++) moods.add(poseAt(s.move, (s.rep * k) / 8, s.tempo, s.amp, 1).mood);
    for (const m of moods) this.body.wear(m);
    for (const v of this.body.models.values()) v.m.visible = true;
    this.renderer.compile(this.scene, this.cam);
    for (const v of this.body.models.values()) v.m.visible = v === this.body.cur;
  }

  private stillTime() {
    const { slices } = this.plan;
    const order = ['jazz-hands', 'cheer', 'tada', 'pose', 'wave', 'clap', 'kick', 'twirl', 'hop'];
    const pick = order.map((m) => slices.find((s) => s.move === m)).find(Boolean) || slices[0];
    return pick.t0 + pick.rep * 0.5;
  }

  // the dance moved on by dt, to `t` on its clock (looping)
  private step(dt: number, t: number) {
    const { slices, loop } = this.plan;
    this.clock = t;
    const lt = ((t % loop) + loop) % loop;
    const s = slices.find((x) => lt < x.t0 + x.rep * x.reps) || slices[slices.length - 1];
    const k = Math.max(0, Math.min(s.reps - 1, Math.floor((lt - s.t0) / s.rep)));
    const into = lt - s.t0;
    let P: Pose;
    if (s.i < 0) {
      // the rest: a bob in time, stepping back to the middle
      P = neutral();
      const u = Math.min(1, into / s.rep), b = into / BEAT;
      P.y = 0.04 * Math.max(0, Math.sin((b % 1) * Math.PI)); P.sy = 1 - 0.03 * Math.abs(Math.cos(b * Math.PI)); P.mood = 'smile';
      const e = u * u * (3 - 2 * u);
      this.body.base.position.set(s.offs[0][0] * (1 - e), 0, s.offs[0][1] * (1 - e));
    } else {
      P = poseAt(s.move, into, s.tempo, s.amp, 1);
      P.dx *= s.dirs[k]; P.dz *= s.dirs[k];
      if (s.move === 'conga') P.py = 0;
      this.body.base.position.set(s.offs[k][0], 0, s.offs[k][1]);
      // the effects the move sends (hearts, sparkles, notes), since the last frame
      const from = Math.max(0, into - dt);
      if (dt > 0 && from < into) for (const c of cuesBetween(s.move, from, into, s.tempo)) this.cue(c as any);
    }
    this.body.apply(P, t);
    this.fx.update(dt);
    if (s.i !== this.lit) { this.lit = s.i; this.o.onBeat?.(s.i); }
  }

  private cue([, kind, data]: [number, string, any?]) {
    const head = this.body.head(), n = typeof data === 'number' ? data : data?.n;
    const fwd = new THREE.Vector3(0, 0, 1).applyQuaternion(this.body.root.quaternion);
    if (kind === 'boop') this.fx.hearts(head.clone().addScaledVector(fwd, 0.55).add(new THREE.Vector3(0, -0.7, 0)), 2, 0.32);
    else if (kind === 'spark') this.fx.burst(head.clone().addScaledVector(fwd, 0.5).add(new THREE.Vector3(0, -0.2, 0)));
    else if (kind === 'clap') this.fx.sparkles(head.clone().addScaledVector(fwd, 0.7).add(new THREE.Vector3(0, -1.05, 0)), '#fff2b0', 6, 0.16);
    else if (kind === 'notes') this.fx.notes(head, n ?? 2);
    else if (kind === 'hearts') this.fx.hearts(head.clone().add(new THREE.Vector3(0, -0.2, 0)), n ?? 3);
    else if (kind === 'sparkles') this.fx.sparkles(head, data?.color, n ?? 10, data?.spread ?? 0.5);
    else if (kind === 'confetti') this.fx.confetti(head.clone().add(new THREE.Vector3(0, 0.6, 0)), 30, 0);
    else if (kind === 'zzz') this.fx.zzz(head);
    else if (kind === 'emoji') this.fx.emoji(head.clone().add(new THREE.Vector3(0, 0.3, 0)), typeof data === 'string' ? data : data?.text, 0.45);
    else this.fx.sparkles(head, '#ffffff', 6, 0.4);
  }

  // the dance's drums, a little ahead of time, only while it plays and sound is on
  private sound() {
    if (!engine.live || !this.playing || this.disposed) { this.cursor = this.clock; return; }
    const ctx = engine.ctx!;
    if (!this.out) { this.out = gain(ctx, 0.85); this.out.connect(engine.duck.beats); }
    const { steps, loop } = this.plan;
    const now = this.clock, horizon = now + 0.14 * this.speed;
    if (this.cursor < now - 0.2) this.cursor = now; // after a pause or a slow frame: no catching up
    const late = ctx.currentTime + 0.004;
    for (let n = Math.floor(this.cursor / loop); n * loop <= horizon; n++) {
      for (const s of steps) {
        const at = n * loop + s.at;
        if (at <= this.cursor || at > horizon) continue;
        const st = s.stepSecs / this.speed;
        const when = ctx.currentTime + (at - now) / this.speed + swingOf(this.pattern, s.idx, st);
        if (when < late) continue;
        playStep(ctx, this.out, this.pattern, s.idx, when, { kit: this.o.kit || 'soft', night: 0, drop: s.drop, accent: s.accent });
        if (s.fill) playFill(ctx, this.out, when, st, 0.7);
      }
    }
    this.cursor = horizon;
  }

  setPlaying(on: boolean) { this.playing = on; if (!on && this.out) { this.out.disconnect(); this.out = null; } }
  setSpeed(x: number) { this.speed = x; }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.ro?.disconnect();
    if (this.out) { try { this.out.disconnect(); } catch { /* gone */ } this.out = null; }
    this.scene.remove(this.fx.root); // its shapes are shared with every other effect on the page
    (this.scene.environment as any)?.dispose?.();
    this.scene.traverse((o: any) => {
      o.geometry?.dispose?.();
      const ms = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
      for (const m of ms) { for (const k in m) { const v = m[k]; if (v && v.isTexture) v.dispose(); } m.dispose?.(); }
    });
    (this.scene.background as any)?.dispose?.();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
    this.canvas.remove();
  }
}
